/**
 * Catalog Admin API Service
 *
 * Admin API methods for catalog management. Separate from catalog.ts (public search/GPC).
 * Uses existing coreAPI client — all requests go via Gateway (:6850) → Catalog (:6890).
 *
 * handleResponse unwraps { success, data } → returns data.
 * coreAPI baseURL is '/api/v1' — paths here start from '/catalog/...'.
 */

import { coreAPI } from './client';
import type {
  // Common
  AdminPaginatedResponse,
  // Brands
  Brand,
  BrandListParams,
  CreateBrandPayload,
  UpdateBrandPayload,
  // Categories
  AdminCategory,
  CategoryTreeResponse,
  CategoryBreadcrumb,
  CreateCategoryPayload,
  UpdateCategoryPayload,
  // Product Models
  ProductModel,
  ProductModelListItem,
  ProductModelListParams,
  CreateProductModelPayload,
  UpdateProductModelPayload,
  ModelMerchantProduct,
  // Model Features
  GroupedFeatures,
  // Merchant Products
  MerchantProduct,
  MerchantProductListItem,
  MerchantProductListParams,
  SuspendProductPayload,
  RejectProductPayload,
  // Admin Products (cross-merchant)
  AdminProductListItem,
  AdminProductListParams,
  // Moderation
  ModerationRequest,
  ModerationListParams,
  ApproveModerationPayload,
  RejectModerationPayload,
  // Enrichment
  EnrichRequestPayload,
  EnrichProductResponse,
  EnrichBatchResponse,
  EnrichmentStats,
  EnrichmentJob,
  EnrichmentJobListParams,
  EnrichmentJobListResponse,
  // Worker Control (EWK-4)
  WorkerStatus,
  WorkerConfigUpdate,
  // Icecat Categories
  IcecatCategoryCacheStats,
  IcecatCategorySearchResponse,
  IcecatCategoryChildrenResponse,
  IcecatCategoryDetail,
  IcecatAutoCreatedListResponse,
  EnsureCategoryPayload,
  EnsureCategoryResult,
  EnsureBatchPayload,
  EnsureBatchResponse,
  // Spec Templates
  SpecTemplate,
  SpecTemplateResponse,
  CreateSpecTemplatePayload,
  // Languages
  SupportedLanguage,
  CreateLanguagePayload,
  UpdateLanguagePayload,
  // Translations
  TranslatePayload,
  TranslateResponse,
  UpdateTranslationPayload,
  UpdateTranslationResponse,
  TranslationAuditParams,
  TranslationAuditResponse,
} from '@/pages/CatalogAdminPage/types/catalog';

import type {
  PaginatedResponse,
  GpcSegment,
  GpcHierarchyNode,
  GpcHierarchyWithPath,
  GpcBrickAttribute,
} from '@/types/catalog';

// ============================================================================
// Helpers
// ============================================================================

function toQuery(params: object): string {
  const sp = new URLSearchParams();
  for (const [key, val] of Object.entries(params as Record<string, unknown>)) {
    if (val !== undefined && val !== null && val !== '') {
      sp.append(key, String(val));
    }
  }
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

// ============================================================================
// API Service
// ============================================================================

export const catalogAdminApi = {
  // ==========================================================================
  // Brands — /catalog/brands (GET public, POST/PATCH admin-guarded in backend)
  // ==========================================================================

  listBrands: ({ is_verified, ...rest }: BrandListParams = {}) =>
    coreAPI.get<AdminPaginatedResponse<Brand>>(
      `/catalog/brands${toQuery({ ...rest, verified_only: is_verified })}`
    ),

  getBrand: (brandGuid: string) =>
    coreAPI.get<Brand>(`/catalog/brands/${brandGuid}?include_translations=true`),

  createBrand: (data: CreateBrandPayload) =>
    coreAPI.post<Brand>('/catalog/brands', data),

  updateBrand: (brandGuid: string, data: UpdateBrandPayload) =>
    coreAPI.patch<Brand>(`/catalog/brands/${brandGuid}`, data),

  // ==========================================================================
  // Categories — /catalog/categories
  // ==========================================================================

  listCategories: (params: Record<string, unknown> = {}) =>
    coreAPI.get<CategoryTreeResponse>(`/catalog/categories${toQuery(params)}`),

  getCategory: (categoryGuid: string) =>
    coreAPI.get<AdminCategory>(`/catalog/categories/${categoryGuid}?include_translations=true`),

  getCategoryChildren: (categoryGuid: string) =>
    coreAPI.get<{ items: AdminCategory[]; total: number }>(`/catalog/categories/${categoryGuid}/children?include_translations=true`),

  getCategoryBreadcrumbs: (categoryGuid: string) =>
    coreAPI.get<CategoryBreadcrumb[]>(`/catalog/categories/${categoryGuid}/breadcrumbs`),

  createCategory: (data: CreateCategoryPayload) =>
    coreAPI.post<AdminCategory>('/catalog/categories', data),

  updateCategory: (categoryGuid: string, data: UpdateCategoryPayload) =>
    coreAPI.patch<AdminCategory>(`/catalog/categories/${categoryGuid}`, data),

  // ==========================================================================
  // Spec Templates — /catalog/admin/categories/:guid/spec-templates (CS-6)
  // ==========================================================================

  getSpecTemplates: (categoryGuid: string) =>
    coreAPI.get<SpecTemplateResponse>(`/catalog/admin/categories/${categoryGuid}/spec-templates`),

  createSpecTemplate: (categoryGuid: string, data: CreateSpecTemplatePayload) =>
    coreAPI.post<SpecTemplate>(`/catalog/admin/categories/${categoryGuid}/spec-templates`, data),

  updateSpecTemplate: (categoryGuid: string, templateGuid: string, data: Partial<CreateSpecTemplatePayload>) =>
    coreAPI.put<SpecTemplate>(`/catalog/admin/categories/${categoryGuid}/spec-templates/${templateGuid}`, data),

  deleteSpecTemplate: (categoryGuid: string, templateGuid: string) =>
    coreAPI.delete(`/catalog/admin/categories/${categoryGuid}/spec-templates/${templateGuid}`),

  regenerateSpecTemplates: (categoryGuid: string) =>
    coreAPI.post<{ deletedAutoTemplates: number; remainingManualTemplates: number }>(`/catalog/admin/categories/${categoryGuid}/spec-templates/regenerate`),

  // ==========================================================================
  // Product Models — /catalog/models
  // ==========================================================================

  listModels: (params: ProductModelListParams = {}) =>
    coreAPI.get<AdminPaginatedResponse<ProductModelListItem>>(`/catalog/models${toQuery(params)}`),

  getModel: (modelGuid: string) =>
    coreAPI.get<ProductModel>(`/catalog/models/${modelGuid}`),

  createModel: (data: CreateProductModelPayload) =>
    coreAPI.post<ProductModel>('/catalog/models', data),

  updateModel: (modelGuid: string, data: UpdateProductModelPayload) =>
    coreAPI.patch<ProductModel>(`/catalog/models/${modelGuid}`, data),

  deleteModel: (modelGuid: string) =>
    coreAPI.delete<void>(`/catalog/models/${modelGuid}`),

  getModelByGtin: (gtin: string) =>
    coreAPI.get<ProductModel | null>(`/catalog/models/by-gtin/${gtin}`),

  getModelFeatures: (modelGuid: string, lang?: string) =>
    coreAPI.get<GroupedFeatures>(`/catalog/models/${modelGuid}/features${toQuery({ lang })}`),

  getModelMerchantProducts: (modelGuid: string) =>
    coreAPI.get<ModelMerchantProduct[]>(`/catalog/models/${modelGuid}/merchant-products`),

  // ==========================================================================
  // Merchant Products (Admin cross-merchant view) — /catalog/merchants/...
  // ==========================================================================

  listMerchantProducts: (params: MerchantProductListParams = {}) => {
    const { merchant_guid, ...rest } = params;
    if (merchant_guid) {
      return coreAPI.get<AdminPaginatedResponse<MerchantProductListItem>>(
        `/catalog/merchants/${merchant_guid}/products${toQuery(rest)}`
      );
    }
    // Fallback: list all merchant products via admin endpoint if available
    return coreAPI.get<AdminPaginatedResponse<MerchantProductListItem>>(
      `/catalog/admin/products${toQuery(rest)}`
    );
  },

  getMerchantProduct: (merchantGuid: string, productGuid: string) =>
    coreAPI.get<MerchantProduct>(`/catalog/merchants/${merchantGuid}/products/${productGuid}`),

  /** Cross-merchant admin product listing with multi-status filter */
  listAdminProducts: (params: AdminProductListParams = {}) => {
    const { status, ...rest } = params;
    const queryParams: Record<string, unknown> = { ...rest };
    if (status?.length) queryParams.status = status.join(',');
    return coreAPI.get<AdminPaginatedResponse<AdminProductListItem>>(
      `/catalog/admin/products${toQuery(queryParams)}`
    );
  },

  // ==========================================================================
  // Admin Product Actions — /catalog/admin/products
  // ==========================================================================

  suspendProduct: (productGuid: string, data: SuspendProductPayload) =>
    coreAPI.post<MerchantProduct>(`/catalog/admin/products/${productGuid}/suspend`, data),

  unsuspendProduct: (productGuid: string) =>
    coreAPI.post<MerchantProduct>(`/catalog/admin/products/${productGuid}/unsuspend`),

  approveProduct: (productGuid: string) =>
    coreAPI.post<MerchantProduct>(`/catalog/admin/products/${productGuid}/approve`),

  rejectProduct: (productGuid: string, data: RejectProductPayload) =>
    coreAPI.post<MerchantProduct>(`/catalog/admin/products/${productGuid}/reject`, data),

  // ==========================================================================
  // GPC Hierarchy — /catalog/gpc (reuses same endpoints as public catalog.ts,
  // but exposed here for admin convenience with consistent typing)
  // ==========================================================================

  getGpcSegments: () =>
    coreAPI.get<PaginatedResponse<GpcSegment>>('/catalog/gpc/segments'),

  getGpcHierarchy: (gpcCode: string) =>
    coreAPI.get<GpcHierarchyWithPath>(`/catalog/gpc/hierarchy/${gpcCode}`),

  getGpcChildren: (gpcCode: string) =>
    coreAPI.get<PaginatedResponse<GpcHierarchyNode>>(`/catalog/gpc/children/${gpcCode}`),

  getGpcBrickAttributes: (brickCode: string) =>
    coreAPI.get<PaginatedResponse<GpcBrickAttribute>>(`/catalog/gpc/bricks/${brickCode}/attributes`),

  searchGpc: (query: string, level?: number, limit = 20) =>
    coreAPI.get<PaginatedResponse<GpcHierarchyNode>>(
      `/catalog/gpc/search${toQuery({ q: query, level, limit })}`
    ),

  // ==========================================================================
  // Enrichment — /catalog/admin/enrichment
  // ==========================================================================

  enrichProduct: (modelGuid: string, data: EnrichRequestPayload = {}) =>
    coreAPI.post<EnrichProductResponse>(`/catalog/admin/enrichment/product/${modelGuid}`, data),

  enrichAll: (data: EnrichRequestPayload = {}) =>
    coreAPI.post<EnrichBatchResponse>('/catalog/admin/enrichment/all', data),

  enrichByBrand: (brandGuid: string, data: EnrichRequestPayload = {}) =>
    coreAPI.post<EnrichBatchResponse>(`/catalog/admin/enrichment/brand/${brandGuid}`, data),

  enrichByCategory: (categoryGuid: string, data: EnrichRequestPayload = {}) =>
    coreAPI.post<EnrichBatchResponse>(`/catalog/admin/enrichment/category/${categoryGuid}`, data),

  getEnrichmentStats: () =>
    coreAPI.get<EnrichmentStats>('/catalog/admin/enrichment/stats'),

  listEnrichmentJobs: (params: EnrichmentJobListParams = {}) =>
    coreAPI.get<EnrichmentJobListResponse>(`/catalog/admin/enrichment/jobs${toQuery(params)}`),

  getEnrichmentJob: (jobGuid: string) =>
    coreAPI.get<EnrichmentJob>(`/catalog/admin/enrichment/jobs/${jobGuid}`),

  cancelEnrichmentJob: (jobGuid: string) =>
    coreAPI.post<EnrichmentJob>(`/catalog/admin/enrichment/jobs/${jobGuid}/cancel`),

  getJobModels: (jobGuid: string, params: { page?: number; limit?: number; status?: string } = {}) =>
    coreAPI.get<{
      items: Array<{
        model_guid: string;
        icecat_id: number | null;
        name: string;
        image_count: number;
        primary_image_url: string | null;
        enrichment_state: string | null;
        error_code: string | null;
        error_message: string | null;
      }>;
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    }>(`/catalog/admin/enrichment/jobs/${jobGuid}/models${toQuery(params)}`),

  // ==========================================================================
  // Worker Control (EWK-4) — /catalog/admin/enrichment/worker
  // ==========================================================================

  getWorkerStatus: () =>
    coreAPI.get<WorkerStatus>('/catalog/admin/enrichment/worker/status'),

  pauseWorker: () =>
    coreAPI.post<WorkerStatus>('/catalog/admin/enrichment/worker/pause'),

  resumeWorker: () =>
    coreAPI.post<WorkerStatus>('/catalog/admin/enrichment/worker/resume'),

  stopWorker: () =>
    coreAPI.post<WorkerStatus>('/catalog/admin/enrichment/worker/stop'),

  startWorker: () =>
    coreAPI.post<WorkerStatus>('/catalog/admin/enrichment/worker/start'),

  updateWorkerConfig: (data: WorkerConfigUpdate) =>
    coreAPI.patch<WorkerStatus>('/catalog/admin/enrichment/worker/config', data),

  // ==========================================================================
  // Retry (EWK-6) — /catalog/admin/enrichment/retry
  // ==========================================================================

  retrySingle: (modelGuid: string) =>
    coreAPI.post<{ reset: number; modelGuids: string[] }>(`/catalog/admin/enrichment/retry/${modelGuid}`),

  retryBatch: (modelGuids: string[]) =>
    coreAPI.post<{ reset: number; modelGuids: string[] }>('/catalog/admin/enrichment/retry-batch', { model_guids: modelGuids }),

  // ==========================================================================
  // Moderation — /catalog/admin/moderation
  // ==========================================================================

  listModerationRequests: (params: ModerationListParams = {}) =>
    coreAPI.get<AdminPaginatedResponse<ModerationRequest>>(
      `/catalog/admin/moderation/requests${toQuery(params)}`
    ),

  getModerationRequest: (requestGuid: string) =>
    coreAPI.get<ModerationRequest>(`/catalog/admin/moderation/requests/${requestGuid}`),

  approveModerationRequest: (requestGuid: string, data: ApproveModerationPayload = {}) =>
    coreAPI.post<ModerationRequest>(
      `/catalog/admin/moderation/requests/${requestGuid}/approve`,
      data
    ),

  rejectModerationRequest: (requestGuid: string, data: RejectModerationPayload) =>
    coreAPI.post<ModerationRequest>(
      `/catalog/admin/moderation/requests/${requestGuid}/reject`,
      data
    ),

  // ==========================================================================
  // Icecat Categories — /catalog/admin/icecat-categories
  // ==========================================================================

  getIcecatCacheStats: () =>
    coreAPI.get<IcecatCategoryCacheStats>('/catalog/admin/icecat-categories/cache/stats'),

  searchIcecatCategories: (query: string) =>
    coreAPI.get<IcecatCategorySearchResponse>(
      `/catalog/admin/icecat-categories/cache/search${toQuery({ q: query })}`
    ),

  getIcecatCategoryChildren: (icecatId: number) =>
    coreAPI.get<IcecatCategoryChildrenResponse>(
      `/catalog/admin/icecat-categories/cache/children/${icecatId}`
    ),

  getIcecatCategory: (icecatId: number) =>
    coreAPI.get<IcecatCategoryDetail>(`/catalog/admin/icecat-categories/cache/${icecatId}`),

  listAutoCreatedCategories: (params: Record<string, unknown> = {}) =>
    coreAPI.get<IcecatAutoCreatedListResponse>(
      `/catalog/admin/icecat-categories/auto-created${toQuery(params)}`
    ),

  getAutoCreatedStats: () =>
    coreAPI.get<{ total: number; by_level: Record<string, number> }>(
      '/catalog/admin/icecat-categories/auto-created/stats'
    ),

  ensureIcecatCategory: (data: EnsureCategoryPayload) =>
    coreAPI.post<EnsureCategoryResult>('/catalog/admin/icecat-categories/ensure', data),

  ensureIcecatCategoryBatch: (data: EnsureBatchPayload) =>
    coreAPI.post<EnsureBatchResponse>('/catalog/admin/icecat-categories/ensure-batch', data),

  // ==========================================================================
  // Languages — /catalog/admin/languages (write), /catalog/languages (read)
  // ==========================================================================

  listLanguages: () =>
    coreAPI.get<SupportedLanguage[]>('/catalog/languages'),

  getLanguage: (code: string) =>
    coreAPI.get<SupportedLanguage>(`/catalog/languages/${code}`),

  createLanguage: (data: CreateLanguagePayload) =>
    coreAPI.post<SupportedLanguage>('/catalog/admin/languages', data),

  updateLanguage: (code: string, data: UpdateLanguagePayload) =>
    coreAPI.patch<SupportedLanguage>(`/catalog/admin/languages/${code}`, data),

  setDefaultLanguage: (code: string) =>
    coreAPI.post<SupportedLanguage>(`/catalog/admin/languages/${code}/set-default`),

  // ==========================================================================
  // Translations — /catalog/admin/translate, /catalog/admin/translations
  // ==========================================================================

  autoTranslate: (data: TranslatePayload) =>
    coreAPI.post<TranslateResponse>('/catalog/admin/translate', data),

  editTranslation: (data: UpdateTranslationPayload) =>
    coreAPI.patch<UpdateTranslationResponse>('/catalog/admin/translations', data),

  getTranslationAudit: (params: TranslationAuditParams = {}) =>
    coreAPI.get<TranslationAuditResponse>(`/catalog/admin/translations/audit${toQuery(params)}`),
};
